import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBoolean } from 'react-use'
import clsx from 'clsx'

import { Plus16, Trash16 } from '@makyoapp/makyo-icon/icon'
import { CountryOption, DeleteModal, Modal, SelectDropdown, Tabs } from '@makyoapp/makyo-ui'

import { getLanguageList, Language } from '@/lib/language-flag'
import { useDbClientContext } from '@/context/__project__/DbClientContext'
import { useReqError } from '@/hooks/useReqError'
import { Info } from '@/components/Info'

interface Props {
  value: string
  onChange(value: string): void
}

export function LayoutLangDropdown({ value, onChange }: Props) {
  const { t } = useTranslation()
  const { db, dbClient, languages, updateLanguages } = useDbClientContext()

  const [isLoading, setIsLoading] = useState('')
  const [show, setShow] = useBoolean(false)
  const [search, setSearch] = useState('')
  const [del, setDel] = useState<Language | undefined>(undefined)
  const printAPIError = useReqError()

  const languageOptions = useMemo(() => {
    return [...languages, { name: 'add', country: '', countryCode: '' } as Language]
  }, [languages])

  const selected = languageOptions.find((opt) => opt?.ids?.locale === value)

  const onDelHide = useCallback(() => {
    setDel(undefined)
  }, [setDel])

  const onHide = useCallback(() => {
    setSearch('')
    setShow(false)
    onDelHide()
  }, [setShow, setSearch, onDelHide])

  const handleAddLanguage = async (lng: Language) => {
    if (dbClient) {
      setIsLoading(lng.ids.locale)
      updateLanguages([...getLanguageList(), lng])
        .then(() => {
          onHide()
        })
        .catch(printAPIError)
        .finally(() => {
          setIsLoading('')
        })
    }
  }

  const handleRemoveLanguage = async () => {
    if (del) {
      if (dbClient) {
        setIsLoading(del.ids.locale)

        updateLanguages(languages.filter((lang) => lang.ids.locale !== del.ids.locale))
          .catch(printAPIError)
          .finally(() => {
            setIsLoading('')
            onDelHide()
          })
      }
    }
  }

  return !db ? null : (
    <div>
      <SelectDropdown
        menuClassName='mk-lang-sdd'
        toggleClassName='min-w-[140px] py-1! text-m-sm'
        options={languageOptions}
        value={selected}
        onChange={(opt, e) => {
          if (opt.name === 'add') {
            e.preventDefault()
            setShow(true)
          } else {
            onChange(opt.ids.locale)
          }
        }}
        optionLabel={(opt) => {
          return [opt.name, opt.country ?? ''].join(' ')
        }}
        renderOption={(opt) => {
          if (opt.name === 'add') {
            return (
              <div className='flex-c'>
                <Plus16 />
                <div>{t('Add/Remove')}</div>
              </div>
            )
          }
          return <CountryOption slug={opt.countryCode} name={opt.name} />
        }}
      />

      <Modal show={show} onHide={onHide}>
        <Modal.Header onCloseClick={onHide}>{t('Manage Database Languages')}</Modal.Header>
        <div className='text-light border-b bdr px-4 py-2'>
          <Info className='text-m-sm max-w-[320px]'>
            {t(
              'Here you can manage the languages for this database. All tables in the database will have the same languages'
            )}
          </Info>
        </div>
        <Modal.Search
          placeholder={t('Search')}
          value={search}
          onChange={(search) => setSearch(search || '')}
          onClear={() => setSearch('')}
        />
        <Modal.Body noPadding>
          <div className=''>
            <Tabs defaultActiveKey='available'>
              <Tabs.Nav className='border-b bdr'>
                <Tabs.NavItem eventKey='available'>{t('Available')}</Tabs.NavItem>
                <Tabs.NavItem eventKey='added'>{t('Added')}</Tabs.NavItem>
              </Tabs.Nav>
              <Tabs.Panel noPadding eventKey='available'>
                <div className='p-2 h-[40vh] flex flex-col overflow-y-scroll'>
                  {getLanguageList()
                    .filter((l) => {
                      // Check if language is already added
                      const isAdded = languageOptions.find((o) => o.name === l.name)
                      if (isAdded) return false

                      const searchTerm = search.toLowerCase()
                      const matchName = l.name.toLowerCase().includes(searchTerm)
                      const matchCountry = l.country.toLowerCase().includes(searchTerm)
                      const matchLocale = Object.values(l.ids).some((id) => id.toLowerCase().includes(searchTerm))

                      return matchName || matchCountry || matchLocale
                    })
                    .map((lng, idx) => (
                      <button
                        onClick={() => handleAddLanguage(lng)}
                        key={idx}
                        className={clsx('px-2 py-1 dark:hover:bg-slate-600 hover:bg-zinc-200 flex-c')}
                      >
                        <CountryOption slug={lng.countryCode} name={lng.name} className='w-full' />
                        {isLoading === lng.ids.locale ? <span className='loading spin' /> : <Plus16 />}
                      </button>
                    ))}
                </div>
              </Tabs.Panel>
              <Tabs.Panel noPadding eventKey='added'>
                <div className='p-2 h-[40vh] flex flex-col overflow-y-scroll'>
                  {languageOptions
                    .slice(0, languageOptions.length - 1)
                    .filter((o) => {
                      const searchTerm = search.toLowerCase()
                      const matchName = o.name.toLowerCase().includes(searchTerm)
                      const matchCountry = o.country.toLowerCase().includes(searchTerm)
                      const matchLocale = Object.values(o.ids || {}).some((id) => id.toLowerCase().includes(searchTerm))

                      return matchName || matchCountry || matchLocale
                    })
                    .map((o, idx) => (
                      <div key={idx} className='flex-c px-2 py-1 dark:hover:bg-slate-600 hover:bg-zinc-200'>
                        <CountryOption slug={o.countryCode} name={o.name} />
                        {o.name !== 'English' && (
                          <button className='red' onClick={() => setDel(o)}>
                            <Trash16 />
                          </button>
                        )}
                      </div>
                    ))}
                </div>
              </Tabs.Panel>
            </Tabs>
          </div>
        </Modal.Body>
      </Modal>

      <DeleteModal
        show={del !== undefined}
        onHide={onDelHide}
        isLoading={isLoading === del?.ids.locale}
        onSubmit={handleRemoveLanguage}
        title={t('Remove Language')}
        continueLabel={t('Yes, delete')}
      >
        <div className='modal-sm'>
          <div className='text-base mb-2 text-light'>{t("Are you sure you'd like to delete this language?")}</div>
          <div>{t('All the data will be deleted forever, you will not be able to restore it')}</div>
        </div>
      </DeleteModal>
    </div>
  )
}
